// @ts-strict-ignore
import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Store } from "@ngrx/store";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { DialogModule } from "primeng/dialog";
import { InputTextModule } from "primeng/inputtext";
import { RadioButtonModule } from "primeng/radiobutton";
import { SelectModule } from "primeng/select";
import { TableModule } from "primeng/table";
import { TooltipModule } from "primeng/tooltip";
import { take } from "rxjs";
import { APIActions } from "src/app/api/ngrx/api.actions.types";
import { APIModel, APIService } from "src/app/api/shared/services/api.service";
import { ESimActions } from "src/app/modules/esim/ngrx/esim.actions.types";
import { EWizDynamicBaseClassComponent } from "src/app/modules/esim/shared/classes/ewiz-dynamic.class";
import { ErrorMessageComponent } from "src/app/modules/esim/shared/components/error-message/error-message.component";
import { AppActions } from "src/app/ngrx/app.actions.types";
import { AppState } from "src/app/ngrx/root.reducer";
import { StatusIconComponent } from "src/app/shared/components/icons/status-icon/status-icon.component";
import { EWizValidator } from "src/app/shared/constants/validators.constants";
import { ErrorCheck } from "src/app/shared/models/error-check.model";
import * as ProfileModel from "src/app/shared/models/profile.model";
import {
  DropDownDto,
  JavaAppletDedicatedFileParametersDto,
  JavaAppletElementaryFileParametersDto,
  JavaAppletsDto
} from "src/app/shared/models/profile.model";
import { ProgressCheck } from "src/app/shared/models/progress-check.model";
import { SharedDirectivesModule } from "src/app/shared/shared-directives.module";
import { AppletService } from "src/app/shared/services/applet.service";
import { DataService } from "src/app/shared/services/data.service";
import { LogService } from "src/app/shared/services/log.service";
import { ValidatorService } from "src/app/shared/services/validator.service";
import * as C from "../../../../../../shared/constants/globals.constants";

export interface AppletDisplayDto {
  appletId: number;
  name: string;
  description: string;
  fileName: string;
  status: string;
  warehouseStatus: string;
  deleted?: boolean;
}

export interface DedicatedFileDisplayDto {
  id: number;
  fileName: string;
  fileId: string;
  status: string;
}

export interface ElementaryFileDisplayDto {
  id: number;
  fileName: string;
  fileId: string;
  status: string;
}

@UntilDestroy()
@Component({
  selector: C.EWIZ_JAVA_Applet_UPLOAD,
  templateUrl: "./java-applet-upload.component.html",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    DialogModule,
    InputTextModule,
    RadioButtonModule,
    SelectModule,
    TableModule,
    TooltipModule,
    SharedDirectivesModule,
    ErrorMessageComponent,
    StatusIconComponent
  ]
})
export class EWizJavaAppletUploadComponent extends EWizDynamicBaseClassComponent implements OnInit, OnDestroy, ErrorCheck, ProgressCheck {
  constructor(
    private dataService: DataService,
    private store: Store<AppState>,
    private apiService: APIService,
    private logService: LogService,
    private appletService: AppletService,
    private validatorService: ValidatorService
  ) {
    super();

    this.locationOfFileOptions = [
      { label: $localize`MF`, value: "1" },
      { label: $localize`USIM`, value: "2" },
      { label: $localize`ISIM`, value: "3" },
      { label: $localize`DF (requires selecting file)`, value: "4" }
    ];

    this.accessRuleOptions = [
      { label: $localize`ALWAYS`, value: "1" },
      { label: $localize`NEVER`, value: "2" },
      { label: $localize`PIN1`, value: "3" },
      { label: $localize`ADM1`, value: "4" }
    ];

    this.accessRuleOptionsEF = [
      { label: $localize`ALWAYS`, value: "1" },
      { label: $localize`NEVER`, value: "2" },
      { label: $localize`PIN1`, value: "3" },
      { label: $localize`ADM1`, value: "4" },
      { label: $localize`PIN2`, value: "5" }
    ];

    for (let i = 1; i <= 13; i++) {
      this.accessRulePresets.push({
        label: i,
        value: `${i}`
      });
    }
  }

  appletsDisplay: AppletDisplayDto[] = [];
  selectedApplets: AppletDisplayDto[] | null = null;
  dedicatedFiles: DedicatedFileDisplayDto[] = [];
  selectedDedicatedFiles: DedicatedFileDisplayDto[] | null = null;
  elementaryFiles: ElementaryFileDisplayDto[] = [];
  selectedElementaryFiles: ElementaryFileDisplayDto[] | null = null;
  displayCreateJavaAppletDialog = false;
  selectableApplets = [];
  selectedApplet = null;
  editNewApplet = true;
  editAppletMode = false;
  editDedicatedFileMode = false;
  editElementaryFileMode = false;
  editAppletId = null;
  editDedicatedFileId = null;
  editElementaryFileId = null;
  applet: JavaAppletsDto | null = null;
  dedicatedFile: JavaAppletDedicatedFileParametersDto | null = null;
  elementaryFile: JavaAppletElementaryFileParametersDto | null = null;
  editedProfile = null;
  editingProfileId = null;
  reservedFileIds: number[] = [
    0x7fff, 0x7f40, 0x7f41, 0x7f42, 0x7f43, 0x7f44, 0x7f45, 0x7f46, 0x7f47, 0x7f48, 0x7f49, 0x7f4a, 0x7f4b, 0x7f4c, 0x7f4d, 0x7f4e, 0x7f4f,
    0x5f10, 0x5f11, 0x5f12, 0x5f13, 0x5f14, 0x5f15, 0x5f16, 0x5f17, 0x5f18, 0x5f19, 0x5f1a, 0x5f1b, 0x5f1c, 0x5f1d, 0x5f1e, 0x5f1f, 0x5f20,
    0x5f21, 0x5f22, 0x5f23, 0x5f24, 0x5f25, 0x5f26, 0x5f27, 0x5f28, 0x5f29, 0x5f2a, 0x5f2b, 0x5f2c, 0x5f2d, 0x5f2e, 0x5f2f, 0x7f10, 0x7f11,
    0x7f20, 0x7f21, 0x7f22, 0x7f23, 0x7f24, 0x7f25, 0x7f26, 0x7f27, 0x7f28, 0x7f29, 0x7f2a, 0x7f2b, 0x7f2c, 0x7f2d, 0x7f2e, 0x7f2f, 0x7f31,
    0x7f80, 0x7f90
  ];
  locationOfFileOptions = [];
  accessRuleOptions = [];
  accessRuleOptionsEF = [];
  accessRulePresets = [];
  errorMessageDedicatedFiles = "";
  errorFoundForDedicatedFiles = false;
  errorMessageDedicatedFile = "";
  errorFoundForDedicatedFile = false;
  errorMessageElementaryFiles = "";
  errorFoundForElementaryFiles = false;
  errorMessageElementaryFile = "";
  errorFoundForElementaryFile = false;
  nextDedicatedFileId = 1;
  nextElementaryFileId = 1;
  nextFileContentId = 1;
  allApplets: APIModel.JavaAppletDto[] = [];
  showAccessRuleHelp = false;
  elementaryFileMaxLength = null;
  dedicatedFileLocationOptions: DropDownDto[] = [];
  fileSizeMax = 255;
  recordLengthMax = 255;
  elementaryFileSizeHex = "";
  elementaryFileRecordLengthHex = "";

  ngOnInit() {
    this.profileValues.javaAppletsVisited = true;

    this.errorCheck(this.profileValues, this.dataService, this.appletService, this.store);
    this.handleFormEvents();
    this.refreshAppletDisplay();
    this.refreshSelectableApplets();

    this.store
      .select("esim")
      .pipe(untilDestroyed(this))
      .subscribe((esimState) => {
        this.editingProfileId = esimState.editingProfileId;

        if (esimState.overrideNavigationBackTriggered) {
          this.store.dispatch(ESimActions.SetOverrideNavigationBackTriggered({ overrideNavigationBackTriggered: false }));
          this.navigateBack();
        }
        if (esimState.overrideNavigationNextTriggered) {
          this.store.dispatch(ESimActions.SetOverrideNavigationNextTriggered({ overrideNavigationNextTriggered: false }));
          this.navigateNext();
        }
      });

    this.store
      .select("api")
      .pipe(untilDestroyed(this))
      .subscribe((apiState) => {
        if (!apiState.javaApplets) {
          this.store.dispatch(AppActions.ShowBlockUI({ showSpinner: true }));
          this.getAllApplets();
        } else {
          this.allApplets = [...apiState.javaApplets];
          this.refreshAppletDisplay();
          this.refreshSelectableApplets();
        }
      });
  }

  ngOnDestroy() {
    this.store.dispatch(ESimActions.SetOverrideNavigationControls({ overrideNavigationControls: false }));
  }

  getAllApplets() {
    this.allApplets = [];
    this.apiService.Applet_getAllApplets().subscribe({
      next: (response) => {
        const newApplets: APIModel.JavaAppletDto[] = [];

        for (const applet of response) {
          newApplets.push({
            ...applet,
            loadBlockObject: this.appletService.trimLoadBlockObject(applet.loadBlockObject),
            createdDateDisplay: new Date(applet.createdOnUtc),
            updatedDateDisplay: new Date(applet.updatedOnUtc),
            status: this.appletService.validate(applet).status
          });
        }

        this.store.dispatch(APIActions.ClearJavaApplets());
        this.store.dispatch(
          APIActions.CreateJavaApplets({
            applets: [...newApplets]
          })
        );
        this.store.dispatch(AppActions.HideBlockUI());

        this.allApplets = [...newApplets];
        this.refreshAppletDisplay();
        this.refreshSelectableApplets();
      },
      error: (error) => {
        this.logService.log("[EWizJavaAppletUploadComponent] getAllApplets() Error response from Applet_getAllApplets()", error);
        this.store.dispatch(AppActions.HideBlockUI());
      }
    });
  }

  errorCheck(values: ProfileModel.ProfileDto, dataService: DataService, appletService: AppletService, store: Store<AppState>) {
    // Always reset error values to default state prior to executing check
    this.errorFound = false;
    this.errorMessage = "";

    store
      .select("api")
      .pipe(take(1))
      .subscribe((apiState) => {
        if (apiState.javaApplets) {
          let appletsFound = 0;

          for (const javaApplet of values.javaApplets) {
            for (const applet of apiState.javaApplets) {
              if (applet.id === javaApplet.appletId) {
                const validationResult = appletService.validate(applet);
                appletsFound++;

                if (validationResult.status === "error") {
                  this.errorFound = true;
                  this.errorMessage =
                    $localize`Error with configuration in Applet Warehouse for applet` +
                    ` '${applet.name}': ${validationResult.errorMessage}`;
                }

                break;
              }
            }
          }

          if (appletsFound !== values.javaApplets.length) {
            this.errorFound = true;
            this.errorMessage = $localize`One or more applets used in this profile have been deleted`;
          }
        }
      });

    if (this.errorFound) {
      return true;
    }

    for (const javaApplet of values.javaApplets) {
      if (this.errorCheckApplet(javaApplet, dataService)) {
        this.errorFound = true;
      }
    }

    // Special trigger error check for the loaded applet to get any error messages specific to it rendered
    if (this.editAppletMode) {
      this.errorCheckApplet(this.applet, this.dataService);

      // Forces a recheck of the currently loaded DF or EF file so any errors specific to it are shown
      if (this.editDedicatedFileMode && this.editDedicatedFileId) {
        this.errorCheckAppletDedicatedFile(this.applet, this.dedicatedFile, this.dataService);
      } else if (this.editElementaryFileMode && this.editElementaryFileId) {
        this.errorCheckAppletElementaryFile(this.applet, this.elementaryFile, this.dataService);
      }
    }

    if (this.errorFound) {
      this.errorMessage = $localize`Errors found with one or more applets, please check applet(s) marked with errors and correct as needed.`;
    }

    return this.errorFound;
  }

  errorCheckApplet(javaApplet: JavaAppletsDto, dataService: DataService) {
    let dedicatedFileErrors = false;
    let elementaryFileErrors = false;

    dedicatedFileErrors = this.errorCheckAppletDedicatedFiles(javaApplet, dataService);
    elementaryFileErrors = this.errorCheckAppletElementaryFiles(javaApplet, dataService);

    if (dedicatedFileErrors || elementaryFileErrors) {
      return true;
    } else {
      return false;
    }
  }

  errorCheckAppletDedicatedFiles(javaApplet: JavaAppletsDto, dataService: DataService) {
    this.errorFoundForDedicatedFiles = false;
    this.errorMessageDedicatedFiles = "";

    if (+javaApplet.requiresDedicatedFile === 1 && javaApplet.dedicatedFiles) {
      for (const dedicatedFile of javaApplet.dedicatedFiles) {
        const errorFound = this.errorCheckAppletDedicatedFile(javaApplet, dedicatedFile, dataService);

        if (errorFound) {
          this.errorFoundForDedicatedFiles = true;
          this.errorMessageDedicatedFiles = $localize`Errors found with one or more dedicated files, please check files(s) marked with errors and correct as needed.`;
        }
      }
    }

    return this.errorFoundForDedicatedFiles;
  }

  errorCheckAppletDedicatedFile(javaApplet: JavaAppletsDto, dedicatedFile: JavaAppletDedicatedFileParametersDto, dataService: DataService) {
    this.errorFoundForDedicatedFile = false;
    this.errorMessageDedicatedFile = "";

    if (!dataService.isNullOrEmpty(dedicatedFile.fileId)) {
      // FileID cannot be empty or out of range
      if (!dataService.validHexValue(dedicatedFile.fileId, 2, 2)) {
        this.errorMessageDedicatedFile = $localize`File ID values must be 2 HEX bytes in length`;
        this.errorFoundForDedicatedFile = true;
      }
      if (!dataService.hexValueInRange(dedicatedFile.fileId, 0x0000, 0xffff)) {
        this.errorMessageDedicatedFile = $localize`File ID value must be between 0000 and FFFF`;
        this.errorFoundForDedicatedFile = true;
      }

      // FileID cannot match any reserved ones
      for (const fileId of this.reservedFileIds) {
        if (+dedicatedFile.fileId === +fileId) {
          this.errorMessageDedicatedFile = $localize`The specified File ID is reserved and cannot be used.`;
          this.errorFoundForDedicatedFile = true;
        }
      }

      // FileID cannot be used by any other dedicated file for this applet
      if (+javaApplet.requiresDedicatedFile === 1) {
        for (const file of javaApplet.dedicatedFiles) {
          if (file.id !== dedicatedFile.id) {
            if (!dataService.isNullOrEmpty(file.fileId)) {
              if (dedicatedFile.fileId === file.fileId) {
                this.errorMessageDedicatedFile = $localize`File ID can only be used by one dedicated or elementary file within an applet's configuraiton.`;
                this.errorFoundForDedicatedFile = true;
              }
            }
          }
        }
      }

      // FileID cannot be used by any other elementary file for this applet
      if (+javaApplet.requiresDedicatedFile === 1) {
        for (const file of javaApplet.elementaryFiles) {
          if (!dataService.isNullOrEmpty(file.fileId)) {
            if (dedicatedFile.fileId === file.fileId) {
              this.errorMessageDedicatedFile = $localize`File ID can only be used by one dedicated or elementary file within an applet's configuraiton.`;
              this.errorFoundForDedicatedFile = true;
            }
          }
        }
      }
    }

    // DFs that are located in a DF can be located in themselves
    if (+javaApplet.requiresDedicatedFile === 1) {
      if (+dedicatedFile.locationOfFile?.value === 4 && +dedicatedFile.locatedInDF.value === dedicatedFile.id) {
        this.errorMessageDedicatedFile = $localize`Cannot locate a dedicated file within itself, please specify an alternate file or location.`;
        this.errorFoundForDedicatedFile = true;
      }
    }

    // Check for maximum location path depth and that location path root is MF/USIM/ISIM
    const pathCheck = this.errorCheckFilePath(javaApplet, dedicatedFile);

    if (pathCheck.errorFound) {
      this.errorMessageDedicatedFile = pathCheck.errorMessage;
      this.errorFoundForDedicatedFile = true;
    }

    return this.errorFoundForDedicatedFile;
  }

  errorCheckAppletElementaryFiles(javaApplet: JavaAppletsDto, dataService: DataService) {
    this.errorFoundForElementaryFiles = false;
    this.errorMessageElementaryFiles = "";

    if (+javaApplet.requiresElementaryFile === 1) {
      for (const elementaryFile of javaApplet.elementaryFiles) {
        const errorFound = this.errorCheckAppletElementaryFile(javaApplet, elementaryFile, dataService);

        if (errorFound) {
          this.errorFoundForElementaryFiles = true;
          this.errorMessageElementaryFiles = $localize`Errors found with one or more elementary files, please check files(s) marked with errors and correct as needed.`;
        }
      }
    }

    return this.errorFoundForElementaryFiles;
  }

  errorCheckAppletElementaryFile(
    javaApplet: JavaAppletsDto,
    elementaryFile: JavaAppletElementaryFileParametersDto,
    dataService: DataService
  ) {
    this.errorFoundForElementaryFile = false;
    this.errorMessageElementaryFile = "";

    if (!dataService.isNullOrEmpty(elementaryFile.fileId)) {
      // FileID cannot be empty or out of range
      if (!dataService.validHexValue(elementaryFile.fileId, 2, 2)) {
        this.errorMessageElementaryFile = $localize`File ID values must be 2 HEX bytes in length`;
        this.errorFoundForElementaryFile = true;
      }
      if (!dataService.hexValueInRange(elementaryFile.fileId, 0x0000, 0xffff)) {
        this.errorMessageElementaryFile = $localize`File ID value must be between 0000 and FFFF`;
        this.errorFoundForElementaryFile = true;
      }

      // FileID cannot match any reserved ones
      for (const fileId of this.reservedFileIds) {
        if (+elementaryFile.fileId === +fileId) {
          this.errorMessageElementaryFile = $localize`The specified File ID is reserved and cannot be used.`;
          this.errorFoundForElementaryFile = true;
        }
      }

      // FileID cannot be used by any other dedicated file for this applet
      if (+javaApplet.requiresDedicatedFile === 1 && javaApplet.dedicatedFiles) {
        for (const file of javaApplet.dedicatedFiles) {
          if (!dataService.isNullOrEmpty(file.fileId)) {
            if (elementaryFile.fileId === file.fileId) {
              this.errorMessageElementaryFile = $localize`File ID can only be used by one dedicated or elementary file within an applet's configuraiton.`;
              this.errorFoundForElementaryFile = true;
            }
          }
        }
      }

      // FileID cannot be used by any other elementary file for this applet
      if (+javaApplet.requiresDedicatedFile === 1) {
        for (const file of javaApplet.elementaryFiles) {
          if (file.id !== elementaryFile.id) {
            if (!dataService.isNullOrEmpty(file.fileId)) {
              if (elementaryFile.fileId === file.fileId) {
                this.errorMessageElementaryFile = $localize`File ID can only be used by one dedicated or elementary file within an applet's configuraiton.`;
                this.errorFoundForElementaryFile = true;
              }
            }
          }
        }
      }
    }

    if (
      !dataService.isNullOrEmpty(elementaryFile.fileSize) &&
      !dataService.isNullOrEmpty(elementaryFile.recordLength) &&
      elementaryFile.fileSize % elementaryFile.recordLength !== 0
    ) {
      this.errorMessageElementaryFile = $localize`File Size and Record Length must be divisible with no remainder.`;
      this.errorFoundForElementaryFile = true;
    } else {
      for (const fileContent of elementaryFile.fileContent) {
        if (!dataService.isNullOrEmpty(fileContent.content) && fileContent.content.length !== +elementaryFile.recordLength * 2) {
          if (+elementaryFile.fileStructure === 1) {
            this.errorMessageElementaryFile = $localize`File Content must contain the number of hex bytes defined by the File Size.`;
          } else {
            this.errorMessageElementaryFile = $localize`File Content must contain the number of hex bytes defined by the Record Length.`;
          }
          this.errorFoundForElementaryFile = true;
        }
      }
    }

    // If file structure is not set to "Transparent" the record length must be within valid range
    if (
      !dataService.isNullOrEmpty(elementaryFile.fileStructure) &&
      +elementaryFile.fileStructure !== 1 &&
      !dataService.isNullOrEmpty(elementaryFile.recordLength) &&
      (elementaryFile.recordLength < 1 || elementaryFile.recordLength > this.recordLengthMax)
    ) {
      this.errorMessageElementaryFile = $localize`Record Length value must be between 1 and` + ` ${this.recordLengthMax}.`;
      this.errorFoundForElementaryFile = true;
    }

    // The file size must be within valid range
    if (
      !dataService.isNullOrEmpty(elementaryFile.fileStructure) &&
      !dataService.isNullOrEmpty(elementaryFile.fileSize) &&
      (elementaryFile.fileSize < 1 || elementaryFile.fileSize > this.fileSizeMax)
    ) {
      this.errorMessageElementaryFile = $localize`File Size value must be between 1 and` + ` ${this.fileSizeMax}.`;
      this.errorFoundForElementaryFile = true;
    }

    // If EF is located in a DF but DFs aren't required throw an error
    if (+elementaryFile.locationOfFile?.value === 4 && +javaApplet.requiresDedicatedFile !== 1) {
      this.errorMessageElementaryFile = $localize`Elementary file cannot be located in a dedicated file for applets that do not require dedicated files.`;
      this.errorFoundForElementaryFile = true;
    }

    // Check for maximum location path depth and that location path root is MF/USIM/ISIM
    const pathCheck = this.errorCheckFilePath(javaApplet, elementaryFile);

    if (pathCheck.errorFound) {
      this.errorMessageElementaryFile = pathCheck.errorMessage;
      this.errorFoundForElementaryFile = true;
    }

    return this.errorFoundForElementaryFile;
  }

  errorCheckFilePath(javaApplet: JavaAppletsDto, file: JavaAppletDedicatedFileParametersDto | JavaAppletElementaryFileParametersDto) {
    let errorMessage = "";
    let errorFound = false;
    const pathCheck = this.getPathDepthAndSize(javaApplet, file, 0, 0);

    if (pathCheck) {
      if (+file.locationOfFile?.value === 4 && +file.locatedInDF.value !== 0) {
        if (pathCheck.terminal === false) {
          errorMessage = $localize`The location for this file is invalid, the root most entry must be located within either MF, USIM, or ISIM.`;
          errorFound = true;
        } else if (pathCheck.size > 8) {
          errorMessage = $localize`The location for this file is invalid, the current path would exceed 8 bytes in length.`;
          errorFound = true;
        }
      }
    } else {
      errorMessage = $localize`The location for this file is invalid, the current path is a circular reference.`;
      errorFound = true;
    }

    return {
      errorMessage,
      errorFound
    };
  }

  getPathDepthAndSize(
    javaApplet: JavaAppletsDto,
    file: JavaAppletDedicatedFileParametersDto | JavaAppletElementaryFileParametersDto,
    depth = 0,
    size = 0
  ) {
    // Max depth to prevent circular references
    if (depth > 10) {
      return null;
    }

    if (+file.locationOfFile?.value === 4 && +file.locatedInDF.value !== 0) {
      for (const dedicatedFile of javaApplet.dedicatedFiles) {
        if (dedicatedFile.id === +file.locatedInDF.value) {
          return this.getPathDepthAndSize(javaApplet, dedicatedFile, depth + 1, size + 2);
        }
      }
    } else {
      return {
        depth: depth,
        size: size + (+file.locationOfFile?.value === 2 || +file.locationOfFile?.value === 3 ? 2 : 0),
        terminal: +file.locationOfFile?.value >= 1 && +file.locationOfFile?.value <= 3
      };
    }
  }

  progressCheck(values: ProfileModel.ProfileDto, dataService: DataService, appletService: AppletService, store: Store<AppState>) {
    let appletWarehouseIssue = false;

    store.select("api").subscribe((apiState) => {
      if (apiState.javaApplets) {
        for (const javaApplet of values.javaApplets) {
          for (const applet of apiState.javaApplets) {
            if (applet.id === javaApplet.appletId) {
              const validationResult = appletService.validate(applet);

              if (validationResult.status === "incomplete") {
                appletWarehouseIssue = true;
              }

              break;
            }
          }
        }
      }
    });

    if (appletWarehouseIssue) {
      return false;
    }

    if (!values.javaAppletsVisited) {
      return false;
    }

    for (const javaApplet of values.javaApplets) {
      if (!this.progressCheckApplet(javaApplet, dataService)) {
        return false;
      }
    }

    return true;
  }

  progressCheckApplet(javaApplet: JavaAppletsDto, dataService: DataService) {
    if (!javaApplet.requiresDedicatedFile || !javaApplet.requiresElementaryFile) {
      return false;
    }

    if (+javaApplet.requiresDedicatedFile === 1 && javaApplet.dedicatedFiles) {
      if (javaApplet.dedicatedFiles.length === 0) {
        return false;
      }

      for (const dedicatedFile of javaApplet.dedicatedFiles) {
        const progressCheck = this.progressCheckAppletDedicatedFile(javaApplet, dedicatedFile, dataService);

        if (!progressCheck) {
          return false;
        }
      }
    }

    if (+javaApplet.requiresElementaryFile === 1) {
      if (javaApplet.elementaryFiles.length === 0) {
        return false;
      }

      for (const elementaryFile of javaApplet.elementaryFiles) {
        const progressCheck = this.progressCheckAppletElementaryFile(javaApplet, elementaryFile, dataService);

        if (!progressCheck) {
          return false;
        }
      }
    }

    return true;
  }

  progressCheckAppletDedicatedFile(
    javaApplet: JavaAppletsDto,
    dedicatedFile: JavaAppletDedicatedFileParametersDto,
    dataService: DataService
  ) {
    if (dataService.isNullOrEmpty(dedicatedFile.fileId)) {
      return false;
    }

    if (+dedicatedFile.locationOfFile?.value === 4 && +dedicatedFile.locatedInDF.value === 0) {
      return false;
    }

    return true;
  }

  progressCheckAppletElementaryFile(
    javaApplet: JavaAppletsDto,
    elementaryFile: JavaAppletElementaryFileParametersDto,
    dataService: DataService
  ) {
    if (dataService.isNullOrEmpty(elementaryFile.fileId)) {
      return false;
    }

    if (dataService.isNullOrEmpty(elementaryFile.fileStructure)) {
      return false;
    }

    if (dataService.isNullOrEmpty(elementaryFile.fileSize)) {
      return false;
    }

    if (
      (+elementaryFile.fileStructure === 2 || +elementaryFile.fileStructure === 3) &&
      dataService.isNullOrEmpty(elementaryFile.recordLength)
    ) {
      return false;
    }

    if (dataService.isNullOrEmpty(elementaryFile.highUpdateActivity)) {
      return false;
    }

    for (const fileContent of elementaryFile.fileContent) {
      if (dataService.isNullOrEmpty(fileContent.content)) {
        return false;
      }
    }

    if (+elementaryFile.locationOfFile?.value === 4) {
      if (+elementaryFile.locatedInDF.value === 0) {
        return false;
      } else {
        let found = false;

        for (const file of javaApplet.dedicatedFiles) {
          if (+elementaryFile.locatedInDF.value === +file.id) {
            found = true;
            break;
          }
        }

        if (!found) {
          return false;
        }
      }
    }

    return true;
  }

  refreshAppletDisplay() {
    const newAppletsDisplay: AppletDisplayDto[] = [];

    for (const javaApplet of this.profileValues.javaApplets) {
      for (const applet of this.allApplets) {
        if (+javaApplet.appletId === +applet.id) {
          newAppletsDisplay.push({
            appletId: applet.id,
            name: applet.name,
            description: applet.description,
            fileName: applet.fileName,
            status: this.parseAppletStatus(javaApplet, this.dataService),
            warehouseStatus: this.appletService.validate(applet).status
          });
          break;
        }
      }
    }

    this.appletsDisplay = [
      ...newAppletsDisplay,
      ...this.profileValues.javaApplets
        .filter((record) => {
          return !this.allApplets.map((activeRecord) => activeRecord.id).includes(record.appletId);
        })
        .map((applet) => {
          return {
            appletId: applet.appletId,
            name: $localize`Applet Deleted`,
            description: "",
            fileName: "",
            status: "error",
            warehouseStatus: "error",
            deleted: true
          };
        })
    ];
  }

  refreshSelectableApplets() {
    const availableSelectableApplets = [];

    for (const selectableApplet of this.allApplets) {
      let appletFound = false;

      for (const javaApplet of this.profileValues.javaApplets) {
        if (javaApplet.appletId === selectableApplet.id) {
          appletFound = true;
          break;
        }
      }

      if (!appletFound) {
        availableSelectableApplets.push({
          label: `${selectableApplet.name} [${selectableApplet.fileName}]`,
          value: selectableApplet.id
        });
      }
    }

    availableSelectableApplets.sort((a, b) => {
      if (a.label > b.label) {
        return 1;
      } else {
        return -1;
      }
    });

    this.selectableApplets = [...availableSelectableApplets];
  }

  addApplet() {
    this.selectedApplet = null;
    this.displayCreateJavaAppletDialog = true;
  }

  onRequiresDedicatedFileChange(requiresDedicatedFile: string) {
    const requirementChanged = this.applet.requiresDedicatedFile !== requiresDedicatedFile;

    this.applet.requiresDedicatedFile = requiresDedicatedFile;

    if (requirementChanged) {
      this.applet.dedicatedFiles = [];
      this.dedicatedFiles = [];
      this.selectedDedicatedFiles = [];
      this.editDedicatedFileId = null;
      this.dedicatedFile = null;
      this.nextDedicatedFileId = 1;
      this.updateDirectoryFileLocationOptions();
      this.refreshDedicatedFileDisplay();
    }

    this.handleFormEvents();
  }

  onRequiresElementaryFileChange(requiresElementaryFile: string) {
    const requirementChanged = this.applet.requiresElementaryFile !== requiresElementaryFile;

    this.applet.requiresElementaryFile = requiresElementaryFile;

    if (requirementChanged) {
      this.applet.elementaryFiles = [];
      this.elementaryFiles = [];
      this.selectedElementaryFiles = [];
      this.editElementaryFileId = null;
      this.elementaryFile = null;
      this.nextElementaryFileId = 1;
      this.refreshElementaryFileDisplay();
    }

    this.handleFormEvents();
  }

  deleteApplets() {
    const newJavaApplets = [];

    for (const javaApplet of this.profileValues.javaApplets) {
      let deleteApplet = false;

      for (const selectedApplet of this.selectedApplets) {
        if (selectedApplet.appletId === javaApplet.appletId) {
          deleteApplet = true;
        }
      }

      if (!deleteApplet) {
        newJavaApplets.push(javaApplet);
      }
    }

    this.selectedApplets = [];
    this.profileValues.javaApplets = [...newJavaApplets];

    this.saveProfile();
    this.refreshAppletDisplay();
    this.refreshSelectableApplets();
    this.handleFormEvents();
  }

  onCreateJavaApplet() {
    this.displayCreateJavaAppletDialog = false;

    const newApplet = {
      appletId: +this.selectedApplet.value,
      requiresDedicatedFile: null,
      dedicatedFiles: [],
      requiresElementaryFile: null,
      elementaryFiles: []
    };

    this.profileValues.javaApplets.push(newApplet);

    this.refreshAppletDisplay();
    this.refreshSelectableApplets();
    this.handleFormEvents();

    if (this.editNewApplet) {
      this.editApplet(newApplet.appletId);
    }
  }

  onCancelCreateJavaApplet() {
    this.displayCreateJavaAppletDialog = false;
  }

  parseAppletStatus(javaApplet: JavaAppletsDto, dataService: DataService) {
    if (this.errorCheckApplet(javaApplet, dataService)) {
      return "error";
    } else if (!this.progressCheckApplet(javaApplet, dataService)) {
      return "incomplete";
    } else {
      return "complete";
    }
  }

  editApplet(appletId: number) {
    this.store.dispatch(ESimActions.SetOverrideNavigationControls({ overrideNavigationControls: true }));

    for (const javaApplet of this.profileValues.javaApplets) {
      if (javaApplet.appletId === appletId) {
        this.applet = javaApplet;

        this.nextDedicatedFileId = 1;

        // Migrates older profile models over to the array based dedicated files property
        if (!javaApplet.dedicatedFiles) {
          this.applet.dedicatedFiles = [];
        }

        for (const dedicatedFile of javaApplet.dedicatedFiles) {
          if (dedicatedFile.id >= this.nextDedicatedFileId) {
            this.nextDedicatedFileId = dedicatedFile.id + 1;
          }
        }

        this.nextElementaryFileId = 1;

        for (const elementaryFile of javaApplet.elementaryFiles) {
          if (elementaryFile.id >= this.nextElementaryFileId) {
            this.nextElementaryFileId = elementaryFile.id + 1;
          }
        }

        break;
      }
    }

    this.errorCheckApplet(this.applet, this.dataService);
    this.refreshDedicatedFileDisplay();
    this.refreshElementaryFileDisplay();

    this.editAppletMode = true;
    this.editAppletId = appletId;
    this.editDedicatedFileMode = true;
    this.editElementaryFileMode = false;
  }

  navigateBack() {
    this.saveProfile(() => {
      if (this.editElementaryFileMode && this.editElementaryFileId) {
        // Editing an EF and going back to EF management
        this.editElementaryFileId = null;
      } else if (this.editElementaryFileMode && !this.editElementaryFileId) {
        // In EF management and going back to DF management
        this.editElementaryFileMode = false;
        this.editDedicatedFileMode = true;
        this.editDedicatedFileId = null;
      } else if (this.editDedicatedFileMode && this.editDedicatedFileId) {
        // Editing a DF and going back to DF management
        this.editDedicatedFileId = null;
      } else {
        // In DF management and going back to applet management
        this.navigateToManagement();
      }

      if (this.editDedicatedFileMode) {
        this.refreshDedicatedFileDisplay();
      } else if (this.editElementaryFileMode) {
        this.refreshElementaryFileDisplay();
      }
    });
  }

  navigateNext() {
    this.saveProfile(() => {
      if (this.editDedicatedFileMode && !this.editDedicatedFileId) {
        // In DF management and going to EF management
        this.editDedicatedFileMode = false;
        this.editElementaryFileMode = true;
      } else if (this.editDedicatedFileMode && this.editDedicatedFileId) {
        // Editing a DF and going back to DF management
        this.editDedicatedFileId = null;
      } else if (this.editElementaryFileMode && this.editElementaryFileId) {
        // Editing an EF and going back to EF management
        this.editElementaryFileId = null;
      } else {
        // In EF management and going back to applet management
        this.navigateToManagement();
      }

      if (this.editDedicatedFileMode) {
        this.refreshDedicatedFileDisplay();
      } else if (this.editElementaryFileMode) {
        this.refreshElementaryFileDisplay();
      }
    });
  }

  navigateToManagement() {
    this.store.dispatch(ESimActions.SetOverrideNavigationControls({ overrideNavigationControls: false }));
    this.editAppletMode = false;

    this.refreshAppletDisplay();
  }

  saveProfile(callback = null) {
    // Abort saving the profile if it is locked
    if (this.editedProfile?.locked) {
      if (callback) {
        callback();
      }

      return;
    }

    this.overallState = this.validatorService.validateNodeTree(EWizValidator, C.EWIZ_ROOT, this.profileValues);

    this.store.dispatch(AppActions.ShowBlockUI({ showSpinner: true }));

    this.apiService
      .ESimProfile_updateProfile({
        id: this.editedProfile.id,
        name: this.editedProfile.name,
        description: this.editedProfile.description,
        profileJson: this.profileValues,
        locked: this.editedProfile.locked,
        status: this.overallState
      })
      .subscribe({
        next: (response) => {
          this.store.dispatch(AppActions.HideBlockUI());
          this.store.dispatch(
            APIActions.UpdateESimProfile({
              profile: { ...response }
            })
          );

          this.editedProfile = { ...response };

          if (callback) {
            callback();
          }
        },
        error: (error) => {
          this.store.dispatch(AppActions.HideBlockUI());
          this.store.dispatch(
            AppActions.ShowToastMessage({
              severity: "error",
              detail: $localize`Error updating ESim profile`
            })
          );
          this.store.dispatch(AppActions.HideBlockUI());
          this.logService.log("[EWizJavaAppletUploadComponent] saveProfile() Error response from ESimProfile_updateProfile()", error);
        }
      });
  }

  refreshElementaryFileDisplay() {
    const newElementaryFileDisplay: ElementaryFileDisplayDto[] = [];

    for (const elementaryFile of this.applet.elementaryFiles) {
      newElementaryFileDisplay.push({
        id: elementaryFile.id,
        fileName: elementaryFile.fileName,
        fileId: elementaryFile.fileId,
        status: this.parseElementaryFileStatus(this.applet, elementaryFile, this.dataService)
      });
    }

    this.elementaryFiles = [...newElementaryFileDisplay];
  }

  parseElementaryFileStatus(javaApplet: JavaAppletsDto, elementaryFile: JavaAppletElementaryFileParametersDto, dataService: DataService) {
    if (this.errorCheckAppletElementaryFile(javaApplet, elementaryFile, dataService)) {
      return "error";
    } else if (!this.progressCheckAppletElementaryFile(javaApplet, elementaryFile, dataService)) {
      return "incomplete";
    } else {
      return "complete";
    }
  }

  addElementaryFile() {
    this.applet.elementaryFiles.push({
      id: this.nextElementaryFileId,
      fileName: null,
      fileId: null,
      fileStructure: null,
      fileSize: null,
      recordLength: null,
      accessRules: {
        ruleNumber: this.accessRulePresets[0],
        read: this.accessRuleOptionsEF[0],
        update: this.accessRuleOptionsEF[2],
        increase: this.accessRuleOptionsEF[1],
        activate: this.accessRuleOptionsEF[3],
        deactivate: this.accessRuleOptionsEF[3],
        delete: this.accessRuleOptionsEF[3]
      },
      highUpdateActivity: "2",
      fileContent: [],
      locationOfFile: this.locationOfFileOptions[0],
      locatedInDF: { label: $localize`Please select:`, value: "0" }
    });

    this.handleFormEvents();
    this.editElementaryFile(this.nextElementaryFileId);
    this.nextElementaryFileId++;
  }

  deleteElementaryFiles() {
    const newElementaryFiles = [];

    for (const elementaryFile of this.applet.elementaryFiles) {
      let deleteFile = false;

      for (const selectedFile of this.selectedElementaryFiles) {
        if (selectedFile.id === elementaryFile.id) {
          deleteFile = true;
          break;
        }
      }

      if (!deleteFile) {
        newElementaryFiles.push(elementaryFile);
      }
    }

    this.applet.elementaryFiles = [...newElementaryFiles];
    this.selectedElementaryFiles = [];

    this.saveProfile();
    this.handleFormEvents();
    this.updateDirectoryFileLocationOptions();
    this.refreshElementaryFileDisplay();
  }

  editElementaryFile(id: number) {
    this.editElementaryFileId = id;

    for (const elementaryFile of this.applet.elementaryFiles) {
      if (elementaryFile.id === id) {
        this.elementaryFile = elementaryFile;
        break;
      }
    }

    this.updateDirectoryFileLocationOptions();
    this.syncElementaryFileHexValues();
    this.setRecordLengthMinMaxThresholds();
    this.errorCheckAppletElementaryFile(this.applet, this.elementaryFile, this.dataService);
  }

  refreshDedicatedFileDisplay() {
    const newDedicatedFileDisplay: DedicatedFileDisplayDto[] = [];

    for (const dedicatedFile of this.applet.dedicatedFiles) {
      newDedicatedFileDisplay.push({
        id: dedicatedFile.id,
        fileName: dedicatedFile.fileName,
        fileId: dedicatedFile.fileId,
        status: this.parseDedicatedFileStatus(this.applet, dedicatedFile, this.dataService)
      });
    }

    this.dedicatedFiles = [...newDedicatedFileDisplay];
  }

  parseDedicatedFileStatus(javaApplet: JavaAppletsDto, dedicatedFile: JavaAppletDedicatedFileParametersDto, dataService: DataService) {
    if (this.errorCheckAppletDedicatedFile(javaApplet, dedicatedFile, dataService)) {
      return "error";
    } else if (!this.progressCheckAppletDedicatedFile(javaApplet, dedicatedFile, dataService)) {
      return "incomplete";
    } else {
      return "complete";
    }
  }

  addDedicatedFile() {
    this.applet.dedicatedFiles.push({
      id: this.nextDedicatedFileId,
      fileName: "",
      fileId: null,
      accessRules: {
        deleteSelf: this.accessRuleOptions[3],
        terminate: this.accessRuleOptions[2],
        activate: this.accessRuleOptions[3],
        deactivate: this.accessRuleOptions[3],
        createDF: this.accessRuleOptions[3],
        createEF: this.accessRuleOptions[3]
      },
      pinStatus: {
        pin1: true,
        adm1: true,
        pin2: false
      },
      locationOfFile: this.locationOfFileOptions[0],
      locatedInDF: { label: $localize`Please select:`, value: "0" }
    });

    this.updateDirectoryFileLocationOptions();
    this.handleFormEvents();
    this.editDedicatedFile(this.nextDedicatedFileId);
    this.nextDedicatedFileId++;
  }

  deleteDedicatedFiles() {
    const newDedicatedFiles = [];

    for (const dedicatedFile of this.applet.dedicatedFiles) {
      let deleteFile = false;

      for (const selectedFile of this.selectedDedicatedFiles) {
        if (selectedFile.id === dedicatedFile.id) {
          deleteFile = true;
          break;
        }
      }

      if (!deleteFile) {
        newDedicatedFiles.push(dedicatedFile);
      }
    }

    this.applet.dedicatedFiles = [...newDedicatedFiles];
    this.selectedDedicatedFiles = [];

    this.updateDirectoryFileLocationOptions();
    this.saveProfile();
    this.handleFormEvents();
    this.refreshDedicatedFileDisplay();
  }

  editDedicatedFile(id: number) {
    this.editDedicatedFileId = id;

    for (const dedicatedFile of this.applet.dedicatedFiles) {
      if (dedicatedFile.id === id) {
        this.dedicatedFile = dedicatedFile;
        break;
      }
    }

    this.updateDirectoryFileLocationOptions();
    this.errorCheckAppletDedicatedFile(this.applet, this.dedicatedFile, this.dataService);
  }

  updateDirectoryFileLocationOptions() {
    const newOptions = [{ label: $localize`Please select:`, value: "0" }];

    for (const file of this.applet.dedicatedFiles) {
      newOptions.push({
        label: `[${file.id}] ${$localize`File ID`}: '${file.fileId ?? ""}' - ${$localize`File Name`}: '${file.fileName ?? ""}'`,
        value: `${file.id}`
      });
    }

    this.dedicatedFileLocationOptions = [...newOptions];

    // Run through all DF/EF, find any that have location set to 4, if there is a DF id set that isn't in directoryFileLocationOptions reset it to 0
    for (const file of this.applet.dedicatedFiles) {
      if (+file.locationOfFile?.value === 4) {
        let found = false;
        for (const option of this.dedicatedFileLocationOptions) {
          if (+file.locatedInDF.value === +option.value) {
            found = true;
            break;
          }
        }

        if (!found) {
          file.locatedInDF = this.dedicatedFileLocationOptions[0];
        }
      }
    }

    for (const file of this.applet.elementaryFiles) {
      if (+file.locationOfFile?.value === 4) {
        let found = false;
        for (const option of this.dedicatedFileLocationOptions) {
          if (+file.locatedInDF.value === +option.value) {
            found = true;
            break;
          }
        }

        if (!found) {
          file.locatedInDF = this.dedicatedFileLocationOptions[0];
        }
      }
    }
  }

  updateFileContentBlocks() {
    if (+this.elementaryFile.fileStructure === 1) {
      this.elementaryFile.recordLength = this.elementaryFile.fileSize;
      this.elementaryFileRecordLengthHex = this.formatHexByteCount(this.elementaryFile.recordLength);

      if (this.elementaryFile.fileContent.length === 0) {
        this.elementaryFile.fileContent.push({ content: "" });
      } else if (this.elementaryFile.fileContent.length > 1) {
        this.elementaryFile.fileContent = this.elementaryFile.fileContent.slice(0, 1);
      }
    } else if (+this.elementaryFile.fileStructure === 2 || +this.elementaryFile.fileStructure === 3) {
      if (
        !this.dataService.isNullOrEmpty(this.elementaryFile.fileSize) &&
        !this.dataService.isNullOrEmpty(this.elementaryFile.recordLength) &&
        this.elementaryFile.fileSize % this.elementaryFile.recordLength === 0
      ) {
        const records = this.elementaryFile.fileSize / this.elementaryFile.recordLength;

        if (this.elementaryFile.fileContent.length < records) {
          for (let i = this.elementaryFile.fileContent.length, ix = records; i < ix; i++) {
            this.elementaryFile.fileContent.push({ content: "" });
          }
        } else if (this.elementaryFile.fileContent.length > records) {
          this.elementaryFile.fileContent = this.elementaryFile.fileContent.slice(0, records);
        }
      }
    }

    // Technique to get PNG pagination to update dynamically after the above complex array handling
    const newFileContent = [...this.elementaryFile.fileContent];
    this.elementaryFile.fileContent = [...newFileContent];

    this.handleFormEvents();
  }

  onElementaryFileSizeHexChange(fileSizeHex: string) {
    this.elementaryFileSizeHex = this.dataService.parseHexValue(fileSizeHex);
    this.elementaryFile.fileSize = this.parseHexByteCount(this.elementaryFileSizeHex);
    this.updateFileContentBlocks();
  }

  onElementaryFileRecordLengthHexChange(recordLengthHex: string) {
    this.elementaryFileRecordLengthHex = this.dataService.parseHexValue(recordLengthHex);
    this.elementaryFile.recordLength = this.parseHexByteCount(this.elementaryFileRecordLengthHex);
    this.updateFileContentBlocks();
  }

  syncElementaryFileHexValues() {
    this.elementaryFileSizeHex = this.formatHexByteCount(this.elementaryFile?.fileSize);
    this.elementaryFileRecordLengthHex = this.formatHexByteCount(this.elementaryFile?.recordLength);
  }

  formatHexByteCount(value: number | null | undefined) {
    if (this.dataService.isNullOrEmpty(value)) {
      return "";
    }

    return value.toString(16).toUpperCase();
  }

  parseHexByteCount(value: string | null | undefined) {
    if (this.dataService.isNullOrEmpty(value)) {
      return null;
    }

    return parseInt(value, 16);
  }

  setRecordLengthMinMaxThresholds() {
    if (+this.elementaryFile.fileStructure === 2) {
      this.fileSizeMax = 64770;
      this.recordLengthMax = 255;
    } else if (+this.elementaryFile.fileStructure === 3) {
      this.fileSizeMax = 64513;
      this.recordLengthMax = 254;
    } else {
      this.fileSizeMax = 255;
      this.recordLengthMax = 255;
    }

    this.syncElementaryFileHexValues();
    this.updateFileContentBlocks();
  }

  setAccessRulePreset(preset: number) {
    this.elementaryFile.accessRules.ruleNumber = { ...this.accessRulePresets[preset - 1] };
    this.showAccessRuleHelp = false;
    this.updateAccessRulePreset();
  }

  updateAccessRulePreset() {
    const ALWAYS = 0;
    const NEVER = 1;
    const PIN1 = 2;
    const ADM1 = 3;
    const PIN2 = 5;

    switch (+this.elementaryFile.accessRules.ruleNumber.value) {
      case 1: {
        this.elementaryFile.accessRules.read = this.accessRuleOptionsEF[ALWAYS];
        this.elementaryFile.accessRules.update = this.accessRuleOptionsEF[PIN1];
        this.elementaryFile.accessRules.increase = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.activate = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.deactivate = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.delete = this.accessRuleOptionsEF[ADM1];

        break;
      }
      case 2: {
        this.elementaryFile.accessRules.read = this.accessRuleOptionsEF[PIN1];
        this.elementaryFile.accessRules.update = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.increase = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.activate = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.deactivate = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.delete = this.accessRuleOptionsEF[ADM1];

        break;
      }
      case 3: {
        this.elementaryFile.accessRules.read = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.update = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.increase = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.activate = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.deactivate = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.delete = this.accessRuleOptionsEF[ADM1];

        break;
      }
      case 4: {
        this.elementaryFile.accessRules.read = this.accessRuleOptionsEF[ALWAYS];
        this.elementaryFile.accessRules.update = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.increase = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.activate = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.deactivate = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.delete = this.accessRuleOptionsEF[ADM1];

        break;
      }
      case 5: {
        this.elementaryFile.accessRules.read = this.accessRuleOptionsEF[PIN1];
        this.elementaryFile.accessRules.update = this.accessRuleOptionsEF[PIN1];
        this.elementaryFile.accessRules.increase = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.activate = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.deactivate = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.delete = this.accessRuleOptionsEF[ADM1];

        break;
      }
      case 6: {
        this.elementaryFile.accessRules.read = this.accessRuleOptionsEF[PIN1];
        this.elementaryFile.accessRules.update = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.increase = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.activate = this.accessRuleOptionsEF[PIN1];
        this.elementaryFile.accessRules.deactivate = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.delete = this.accessRuleOptionsEF[ADM1];

        break;
      }
      case 7: {
        this.elementaryFile.accessRules.read = this.accessRuleOptionsEF[PIN1];
        this.elementaryFile.accessRules.update = this.accessRuleOptionsEF[PIN1];
        this.elementaryFile.accessRules.increase = this.accessRuleOptionsEF[PIN1];
        this.elementaryFile.accessRules.activate = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.deactivate = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.delete = this.accessRuleOptionsEF[ADM1];

        break;
      }
      case 8: {
        this.elementaryFile.accessRules.read = this.accessRuleOptionsEF[PIN1];
        this.elementaryFile.accessRules.update = this.accessRuleOptionsEF[PIN2];
        this.elementaryFile.accessRules.increase = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.activate = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.deactivate = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.delete = this.accessRuleOptionsEF[ADM1];

        break;
      }
      case 9: {
        this.elementaryFile.accessRules.read = this.accessRuleOptionsEF[ALWAYS];
        this.elementaryFile.accessRules.update = this.accessRuleOptionsEF[PIN1];
        this.elementaryFile.accessRules.increase = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.activate = this.accessRuleOptionsEF[PIN1];
        this.elementaryFile.accessRules.deactivate = this.accessRuleOptionsEF[PIN1];
        this.elementaryFile.accessRules.delete = this.accessRuleOptionsEF[ADM1];

        break;
      }
      case 10: {
        this.elementaryFile.accessRules.read = this.accessRuleOptionsEF[ALWAYS];
        this.elementaryFile.accessRules.update = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.increase = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.activate = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.deactivate = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.delete = this.accessRuleOptionsEF[ADM1];

        break;
      }
      case 11: {
        this.elementaryFile.accessRules.read = this.accessRuleOptionsEF[ALWAYS];
        this.elementaryFile.accessRules.update = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.increase = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.activate = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.deactivate = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.delete = this.accessRuleOptionsEF[NEVER];

        break;
      }
      case 12: {
        this.elementaryFile.accessRules.read = this.accessRuleOptionsEF[PIN1];
        this.elementaryFile.accessRules.update = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.increase = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.activate = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.deactivate = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.delete = this.accessRuleOptionsEF[NEVER];

        break;
      }
      case 13: {
        this.elementaryFile.accessRules.read = this.accessRuleOptionsEF[PIN1];
        this.elementaryFile.accessRules.update = this.accessRuleOptionsEF[PIN1];
        this.elementaryFile.accessRules.increase = this.accessRuleOptionsEF[NEVER];
        this.elementaryFile.accessRules.activate = this.accessRuleOptionsEF[PIN1];
        this.elementaryFile.accessRules.deactivate = this.accessRuleOptionsEF[ADM1];
        this.elementaryFile.accessRules.delete = this.accessRuleOptionsEF[ADM1];

        break;
      }
    }

    this.handleFormEvents();
  }
}
